import React, { useState, useEffect } from 'react';
import { useModal, useStatusMessage, useTranslation } from 'Shared/util/custom-hooks';
import makeComponentTrashable from 'trashable-react';
import Page from 'Shared/components/page/page';
import PageTitle from 'Shared/components/page/page-title';
import InputCheckbox from 'Shared/components/input-checkbox';
import Datepicker from 'Shared/components/datepicker';
import Button from 'Shared/components/button';
import useTranslationContext from 'Contexts/translation-context';
import { exportPageTransactions, exportPageTransactionsSummary, checkAccountPlan, getExportSettings } from 'Shared/util/web-api';
import AccountPlanEditView from 'Pages/accountplan/accountplan-edit-view/accountplan-edit-view';
import { formatDate } from 'Shared/util/formatters';
import StatusMessage from 'Shared/components/status-message';
import ConfirmationModal from 'Shared/components/modals/confirmation-modal';
import { toISODate } from 'Shared/util/export-helper';
import useBrandContext from 'Contexts/brand-context';
import Notice from 'Shared/components/notice';
import styles from './export-transactions-page.css';
import { options, exportOptions } from './export-transactions';
import { handleFileDownloadResponse } from './handleFileDownloadResponse';
import ExoportTransactionsOptions from './export-transactions-options/export-transactions-options';
import ExportTransactionsOptionsSubtitles from './export-transactions-options/export-transactions-options-subtitles';
import { BrandName } from "Shared/util/types";

const ExportTransactionsPage = () => {
  const [statusMessage, dispatchStatusMessage] = useStatusMessage();
  const [selectedOption, setSelectedOption] = useState(options.TODAY);
  const [selectedExportOption, setSelectedExportOption] = useState();
  const [fromDate, setFromDate] = useState(undefined);
  const [toDate, setToDate] = useState(undefined);
  const [selectedYear] = useState(new Date().getFullYear());
  const [hasAccountPlan, setHasAccountPlan] = useState(true);
  const [displayAccountPlan, setDisplayAccountPlan] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [loadingTransactionSummary, setLoadingTransactionSummary] = useState(true);
  const [cacheKey, setCacheKey] = useState(null);
  const [minDate, setMinDate] = useState(undefined);
  const [yesterdayDate, setYesterdayDate] = useState(new Date());
  const [enabledExportOptions, setEnabledExportOptions] = useState({});
  const { translationData } = useTranslationContext();
  const { brandData, loadingBrandData } = useBrandContext();
  const { isModalOpen, openModal, closeModal } = useModal();
  const t = useTranslation(x => x.export);
  const tg = useTranslation(x => x.global);

  useEffect(() => {
    dispatchStatusMessage({ type: 'NONE' });
    checkAccountPlan()
      .then((response) => {
        setHasAccountPlan(response);
        setLoadingTransactions(false);
        setLoadingTransactionSummary(false);
      });

    getExportSettings()
      .then((response) => {
        setMinDate(new Date(response.minDate));
      });

    const date = new Date();
    date.setDate(new Date().getDate() - 1);
    setYesterdayDate(toISODate(date));
  }, []);

  useEffect(() => {
    if (!loadingBrandData) {
      const filterOption = brandData.deActivatedExportOptions;
      (filterOption || []).forEach((opt) => delete exportOptions[opt]);
      setEnabledExportOptions(exportOptions);
      setSelectedExportOption(exportOptions[brandData.defaultExportOption]);
    }
  }, [loadingBrandData]);

  const isChecked = (option) => selectedOption === option;
  const handleOptionClick = (option) => setSelectedOption(option);
  const handleExportOptionClick = (option) => setSelectedExportOption(option);

  const toggleFromDate = (date) => {
    handleOptionClick(options.RANGE);
    setFromDate(date);
  };

  const toggleToDate = (date) => {
    handleOptionClick(options.RANGE);
    setToDate(date);
  };

  const handleFailedResponseMessage = (responseMessage) => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    dispatchStatusMessage({
      type: 'FAILED',
      message: responseMessage,
    });
  }

  const handleExportButtonClick = (ck, showModal) => {
    let startDate;
    let endDate;

    switch (selectedOption) {
      case options.RANGE:
        startDate = fromDate ? toISODate(new Date(fromDate)) : toISODate(new Date(minDate));
        endDate = toDate ? toISODate(new Date(toDate)) : toISODate(new Date());
        break;
      case options.YEAR:
        startDate = toISODate(new Date(selectedYear, 0, 1));
        endDate = toISODate(new Date(selectedYear, 11, 31));
        break;
      default: {
        const start = new Date();
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setDate(end.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        startDate = toISODate(start);
        endDate = toISODate(end);
        break;
      }
    }

    const exportQueryParams = {
      StartDate: startDate,
      EndDate: endDate,
      SaveFormat: selectedExportOption.enumCode,
      CacheKey: ck,
      viewName: [t.exportAccountingDocuments],
      summaryText: [t.summaryText],
      lang: translationData.languageCode,
    };

    setLoadingTransactions(true);
    dispatchStatusMessage({ type: 'NONE' });

    if (selectedExportOption.enumCode === 1 || selectedExportOption.enumCode === 2) {
      setLoadingTransactionSummary(true)
      exportPageTransactionsSummary(exportQueryParams)
        .then((response) => {
          if (response.content) {
            handleFileDownloadResponse(response);
          } else if (!response.success) {
            handleFailedResponseMessage(t[response.message]);
          }
        })
        .finally(() => setLoadingTransactionSummary(false));
    }

    exportPageTransactions(exportQueryParams)
      .then((response) => {
        if (response.content) {
          handleFileDownloadResponse(response);
          setCacheKey(null);
        } else if (!response.success) {
          handleFailedResponseMessage(t[response.message]);
        } else {
          if (showModal) openModal();
          setCacheKey(response.data.cacheKey);
        }
      })
      .finally(() => setLoadingTransactions(false));
  };

  const ExportComponent = brandData?.name === BrandName.FinanceLink
    ? ExportTransactionsOptionsSubtitles
    : ExoportTransactionsOptions;

  return (
    <>
      <Page
        narrowWidth
        title={t.exportAccountingDocuments}
      >
        <StatusMessage
          status={statusMessage.type}
          text={statusMessage.message}
          clearStatus={() => dispatchStatusMessage({ type: 'NONE' })}
          className={styles.statusMessageContainer}
        />
        {!hasAccountPlan && (
          <div className={styles.accountPlanCardContainer}>
            <Notice
              content={t.toExportAccountingRecords}
              button={{
                text: t.activateChartOfAccounts,
                onClick: () => setDisplayAccountPlan(true),
              }}
            />
          </div>
        )}
        <div>
          <div className={styles.exportOptionContainer} onClick={() => handleOptionClick(options.TODAY)}>
            <div className={styles.exportOptionInnerContainer}>
              <div className={styles.flexContainer}>
                <InputCheckbox
                  checked={isChecked(options.TODAY)}
                  onChange={() => handleOptionClick(options.TODAY)}
                  className={styles.customCheckbox}
                />
                <div className={styles.optionText}>
                  <div className={styles.optionTitle}>{t.exportYesterdaysTransactions}</div>
                  <div className={styles.optionDescription}>{formatDate(yesterdayDate)}</div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.exportOptionContainer} onClick={() => handleOptionClick(options.RANGE)}>
            <div className={styles.exportOptionInnerContainer}>
              <div className={styles.flexContainer}>
                <InputCheckbox
                  checked={isChecked(options.RANGE)}
                  onChange={() => handleOptionClick(options.RANGE)}
                  className={styles.customCheckbox}
                />
                <div className={styles.optionText}>
                  <div className={styles.optionTitle}>{t.exportDesiredPeriod}</div>
                  <div className={styles.optionDescription}>{t.chooseFromDateAndToDate}</div>
                </div>
              </div>
              <div className={styles.datepickersContainer} onClick={(e) => e.stopPropagation()}>
                <Datepicker
                  selected={fromDate}
                  onChange={(date) => toggleFromDate(date)}
                  placeholder={tg.from}
                  minDate={minDate}
                  language={translationData.languageCode}
                />
                <Datepicker
                  selected={toDate}
                  onChange={(date) => toggleToDate(date)}
                  placeholder={tg.to}
                  minDate={minDate}
                  popperPlacement="bottom-end"
                  language={translationData.languageCode}
                />
              </div>
            </div>
          </div>

          <PageTitle
            text={t.selectFormatForExport}
            className={styles.exportHeader}
          />
          <ExportComponent
              enabledExportOptions={enabledExportOptions}
              selectedExportOption={selectedExportOption}
              setSelectedExportOption={setSelectedExportOption}
              handleExportOptionClick={handleExportOptionClick}
          />
          <div className={styles.exportButton}>
            <Button
              buttonType="action-primary"
              text={t.exportAccountingDocumentsStar}
              onClick={() => handleExportButtonClick(null, true)}
              attributes={{ disabled: !hasAccountPlan }}
              isLoading={loadingTransactions || loadingTransactionSummary}
            />
          </div>
          {(!loadingBrandData) && (
            <>
              <div className={styles.disclaimer}>
                {t.format(t.customerOrThePersonWithCustomersConsent, { legalName: brandData.legalName, organizationNumber: brandData.organizationNumber })}
              </div>
            </>
          )}
        </div>
      </Page>

      {!hasAccountPlan && (
        <AccountPlanEditView
          show={displayAccountPlan}
          hide={() => setDisplayAccountPlan(false)}
          setHasAccountPlan={setHasAccountPlan}
        />
      )}

      <ConfirmationModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        contentText={t.filesSizeExceeds}
        confirmText={t.continue}
        onConfirm={() => handleExportButtonClick(cacheKey, false)}
      />
    </>
  );
};

export default makeComponentTrashable(ExportTransactionsPage);
